/**
 * PhotoCaptureButton — camera-or-gallery trigger for PhotoAttachment.
 *
 * FT-343 (2026-07-08) initial version. FT-348 (2026-07-08) real install nudge.
 * See DESIGN-BRIEF.md §1.2 + §5.
 *
 * Behaviour:
 *  - Two native file inputs (Camera + Gallery), each with a matching label
 *    styled as a button. The camera input uses `capture="environment"` so iOS
 *    opens the rear-camera picker; gallery input omits `capture`.
 *  - `accept="image/*,image/heic,image/heif"` so iOS gives us HEIC/HEIF blobs
 *    instead of transcoding to JPG at the picker layer (per FT-342 spec).
 *  - Disabled while capture is in progress (`isProcessing`).
 *  - When `photos.length >= maxCount`, both buttons are disabled and a
 *    tooltip surfaces "Max photos reached (N/N)".
 *  - FT-348 install nudge: on the FIRST click of either button in a session
 *    (either camera or gallery), if `usePhotoInstallPrompt.shouldShow` is
 *    true, we PREVENT the native file-picker opening and instead open the
 *    install banner via `showModal()`. The user acknowledges (installed /
 *    remind later) → the banner closes → the SECOND tap opens the file picker
 *    normally. To avoid spam, a component-level `shownThisSession` ref means
 *    we only intercept once per boot per session even if `shouldShow` stays
 *    true.
 *
 * NOTE: the actual pipeline call (usePhotoCapture.processFile / processFiles)
 * is executed by the PhotoAttachment shell — this component only surfaces
 * files via `onFilesPicked`. The install banner is ALSO rendered here so any
 * PhotoAttachment consumer (Equipment / Maintenance / Defects) gets the nudge
 * for free — no per-surface wiring.
 */

import React, { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { Camera, Image as ImageIcon } from 'lucide-react';
import { usePhotoInstallPrompt } from './hooks/usePhotoInstallPrompt';
import { PhotoInstallBanner } from './PhotoInstallBanner';

const ACCEPT = 'image/*,image/heic,image/heif';

/**
 * @param {Object} props
 * @param {(files: File[]) => void} props.onFilesPicked
 * @param {boolean} [props.isProcessing]
 * @param {boolean} [props.disabled]
 * @param {number} props.count           Current photo count.
 * @param {number} props.maxCount        Cap enforced here + in shell.
 * @param {boolean} [props.allowMultiple] Gallery multi-select toggle. Default true.
 * @param {string} [props.testId]
 *
 * FT-349d (2026-07-09): forwardRef exposes `triggerCapture()` so
 * PhotoAttachment can programmatically re-open the camera picker as part of
 * the Replace flow (delete-then-capture from PhotoViewer).
 */
export const PhotoCaptureButton = forwardRef(function PhotoCaptureButton({
  onFilesPicked,
  isProcessing = false,
  disabled = false,
  count,
  maxCount,
  allowMultiple = true,
  testId = 'photo-capture-button',
}, ref) {
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const install = usePhotoInstallPrompt();

  // FT-349d: imperative handle for parent-driven Replace flow. Only exposes
  // the camera input trigger — Replace should always take a fresh photo
  // (matches "delete + take another right now" UX).
  useImperativeHandle(ref, () => ({
    triggerCapture: () => cameraInputRef.current?.click(),
  }), []);
  // "Have we already interrupted a click with the banner this session?"
  // Ref (not state) — we don't want a re-render on flip, we want the guard
  // to update synchronously mid-event.
  const shownThisSessionRef = useRef(false);

  const capReached = typeof maxCount === 'number' && count >= maxCount;
  const isDisabled = disabled || isProcessing || capReached;
  const capLabel = capReached ? `Max photos reached (${count}/${maxCount})` : undefined;

  // Intercept the label click BEFORE the native file picker fires.
  // Returning early with `preventDefault()` cancels the default label→input
  // click chain so the picker stays closed until the banner is dismissed.
  const handleLabelClick = useCallback(
    (e) => {
      if (isDisabled) return;
      if (shownThisSessionRef.current) return;
      if (!install?.shouldShow) return;
      shownThisSessionRef.current = true;
      e.preventDefault();
      e.stopPropagation();
      try {
        install.showModal();
      } catch {
        /* noop */
      }
    },
    [isDisabled, install],
  );

  const handleChange = useCallback(
    (e) => {
      const files = Array.from(e.target.files || []);
      // Reset the input value so picking the SAME file twice still fires onChange.
      e.target.value = '';
      if (files.length === 0) return;
      // Belt+braces cap enforcement at the entry point — never let the shell
      // see more files than the cap allows.
      if (typeof maxCount === 'number') {
        const remaining = Math.max(0, maxCount - count);
        if (remaining === 0) return;
        onFilesPicked?.(files.slice(0, remaining));
      } else {
        onFilesPicked?.(files);
      }
    },
    [count, maxCount, onFilesPicked],
  );

  const btnBase =
    'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500';
  const btnActive =
    'text-white bg-cyan-600 border-cyan-600 hover:bg-cyan-700 cursor-pointer';
  const btnSecondary =
    'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 cursor-pointer';
  const btnDisabled = 'opacity-50 cursor-not-allowed';

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
      {/* Camera capture — mobile primary */}
      <label
        className={`${btnBase} ${isDisabled ? `${btnActive} ${btnDisabled}` : btnActive}`}
        title={capLabel}
        onClick={handleLabelClick}
        data-testid={`${testId}-camera`}
      >
        <Camera className="h-4 w-4" aria-hidden />
        <span>Take photo</span>
        <input
          ref={cameraInputRef}
          type="file"
          accept={ACCEPT}
          capture="environment"
          disabled={isDisabled}
          onChange={handleChange}
          className="hidden"
          data-testid={`${testId}-camera-input`}
        />
      </label>

      {/* Gallery pick — desktop primary / mobile fallback */}
      <label
        className={`${btnBase} ${isDisabled ? `${btnSecondary} ${btnDisabled}` : btnSecondary}`}
        title={capLabel}
        onClick={handleLabelClick}
        data-testid={`${testId}-gallery`}
      >
        <ImageIcon className="h-4 w-4" aria-hidden />
        <span>Choose from gallery</span>
        <input
          ref={galleryInputRef}
          type="file"
          accept={ACCEPT}
          multiple={allowMultiple}
          disabled={isDisabled}
          onChange={handleChange}
          className="hidden"
          data-testid={`${testId}-gallery-input`}
        />
      </label>

      {capReached && (
        <span
          className="text-xs text-gray-500"
          data-testid={`${testId}-cap-label`}
        >
          {capLabel}
        </span>
      )}

      {/* Install banner rendered inline so the picker can be intercepted from
          this component; no per-surface wiring needed. */}
      <PhotoInstallBanner
        isOpen={Boolean(install?.isModalOpen)}
        onInstalled={install?.markInstalled}
        onDismissed={install?.markDismissed}
        testId={`${testId}-install-banner`}
      />
    </div>
  );
});

export default PhotoCaptureButton;
